/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Coordinate } from 'ol/coordinate';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoggerService } from '../../../shared/services/logger.service';
import { Store } from '@ngxs/store';
import { filter, map, pluck, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ZoomLatLonService } from './zoom-lat-lon.service';
import { MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'dash-gis-zoom-lat-lon-dialog',
    templateUrl: './zoom-lat-lon-dialog.component.html',
    styleUrls: ['./zoom-lat-lon-dialog.component.scss'],
    imports: [
        CdkScrollable,
        MatDialogContent,
        FormsModule,
        ReactiveFormsModule,
        LayoutDirective,
        LayoutAlignDirective,
        MatFormField,
        MatInput,
        MatIconButton,
        MatTooltip,
        MatIcon,
    ],
})
export class ZoomLatLonDialogComponent implements OnInit, OnDestroy {
    latlonForm: UntypedFormGroup;
    coordinates: Coordinate;

    // private referenceElement: ElementRef;
    private disposeSubject = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private store: Store,
        private fb: UntypedFormBuilder,
        private zoomService: ZoomLatLonService,
        private ref: MatDialogRef<ZoomLatLonDialogComponent>
    ) {
        this.latlonForm = this.fb.group(
            {
                latlonString: ['', Validators.pattern(this.zoomService.re)],
            },
            { updateOn: 'change' }
        );
    }

    ngOnInit() {
        // const rect: DOMRect = this.referenceElement.nativeElement.getBoundingClientRect();

        // const position = { left: `${ rect.left }px`, top: `${ rect.bottom }px` };
        // this.ref.updatePosition(position);

        this.latlonForm.valueChanges
            .pipe(
                // filter(_ => this.latlonForm.valid),
                pluck('latlonString'),
                map((latlonString) => this.zoomService.parseCoordinates(latlonString)),
                map((coords: number[]) => {
                    if (!coords) {
                        this.latlonForm
                            .get('latlonString')
                            .setErrors([{ latlonString: 'Could not parse coordinates.' }]);
                        return [];
                    }
                    return coords;
                }),
                filter((val) => val.length === 2),
                takeUntil(this.disposeSubject.asObservable())
            )
            .subscribe((coords) => {
                this.coordinates = coords;
            });
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    zoomToLatLon() {
        if (this.coordinates && this.coordinates.length === 2) {
            this.zoomService.zoomToLatLon(this.coordinates[1], this.coordinates[0]);
        }
    }

    closeDialog() {
        this.ref.close(true);
    }
}
